// material-ui
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';

// project imports
import { DRAWER_WIDTH } from 'config';

const TWILIGHT_RAIL_GRADIENT = 'linear-gradient(180deg, #070057 0%, #3e397ca3 100%)';

// `@mui/material/Drawer`'s `.MuiDrawer-paper` is hardcoded to `position:'fixed'` +
// `height:'100%'` UNCONDITIONALLY for every variant (see node_modules/@mui/material/
// Drawer/Drawer.js — the comment there says "temporary style" but it's applied to
// 'permanent' too). Because it's fixed, `height:'100%'` resolves against the
// *viewport* — always exactly 100vh, no matter how tall the actual page content is.
// A fixed pixel offset (`calc(100vh - Npx)`) only lines up with the footer on a page
// that's exactly one viewport tall; any page whose content is even slightly taller
// (e.g. a data table) pushes the real footer further down while this stays rigidly
// pinned to the viewport, leaving a gap. So instead of fighting the fixed position
// with more magic numbers, override `position` back to `'static'` for the permanent
// drawer — it then participates in normal flow and stretches to match the row's
// actual height (via `height:'100%'`, MUI's own default it already declares — see
// above — now resolving against this static ancestor chain instead of the viewport).
// Dashboard/index.jsx now also bounds that row to a fixed height (100vh minus the
// Header/Footer, itself overflow:hidden with only <main> scrolling internally), so
// the row's "actual height" this stretches to is always exactly right regardless of
// how tall the routed page's content is — not just on pages that happen to fit one
// viewport.
const paperOnlyStyle = {
  background: TWILIGHT_RAIL_GRADIENT,
  // marginTop (not paddingTop) — padding only pushes the *content* down while
  // the paper's own box still starts at y:0, so its background/border still
  // extends behind the header (invisibly, but it's really there — visible in
  // DevTools, and would block any future border-radius on this box). Margin
  // makes the box itself start below the header. Height must then subtract
  // the same amount, or the margin just pushes the box 48px past the footer.
  marginTop: '56px', // must match theme.mixins.toolbar.minHeight (themes/index.jsx)
  position: 'static',
  top: 'auto',
  height: 'calc(100% - 56px)'
};

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  borderRight: 'none',

  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),

  overflowX: 'hidden',
  boxShadow: 'none',
  ...theme.applyStyles('dark', { boxShadow: theme.customShadows.z1 })
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),

  overflowX: 'hidden',
  width: theme.spacing(7.5),
  borderRight: 'none',
  boxShadow: theme.customShadows.z1
});

// ==============================|| DRAWER - MINI STYLED ||============================== //

const MiniDrawerStyled = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
  width: DRAWER_WIDTH,
  height: '100%',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: { ...openedMixin(theme), '& .MuiDrawer-paper': { ...openedMixin(theme), ...paperOnlyStyle } }
    },
    {
      props: ({ open }) => !open,
      style: { ...closedMixin(theme), '& .MuiDrawer-paper': { ...closedMixin(theme), ...paperOnlyStyle } }
    }
  ]
}));

export default MiniDrawerStyled;
